import { useState, useEffect } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Modal } from '@/shared/components/ui/Modal';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Select } from '@/shared/components/ui/Select';
import { useMasterItems } from '@/features/master-data/hooks/useMasters';
import { useDebounce } from '@/shared/hooks/useDebounce';
import { api } from '@/shared/api/axios';
import type {
  CompanyType,
  Contact,
  CreateContactPayload,
  CustomerCategoryTier,
  CustomerRelationshipTier,
  CustomerStatus,
  UpdateContactExtendedPayload,
  UpdateContactPayload,
} from '../types';

interface ContactFormModalProps {
  open: boolean;
  contact?: Contact | null; // null/undefined = create mode
  loading?: boolean;
  onClose: () => void;
  /**
   * Two payloads, mirroring the backend's two write paths (see the header
   * comment in ../types.ts): `basic` goes to POST/PATCH /api/contacts,
   * `extended` goes to PATCH /api/contacts-identity/:id and is only ever
   * non-empty once the contact exists (create mode never has an id to PATCH
   * yet — the caller applies `extended` as a follow-up after create succeeds).
   */
  onSubmit: (basic: CreateContactPayload | UpdateContactPayload, extended: UpdateContactExtendedPayload) => void;
  initialValues?: Partial<FormState>;
}

interface FormState {
  name: string;
  company: string;
  position: string;
  email: string;
  phone: string;
  notes: string;
  // ── Extended (BizPlus parity) ────────────────────────────────────────────
  gstin: string;
  panNo: string;
  creditDays: string;
  // ── Account Master (client-approved audit) ───────────────────────────────
  creditLimit: string;
  customerStatus: CustomerStatus;
  customerCategoryTier: CustomerCategoryTier | '';
  customerRelationshipTier: CustomerRelationshipTier | '';
  companyType: CompanyType | '';
  website: string;
  subIndustry: string;
  cinNumber: string;
  iecNumber: string;
  msmeUdyamNumber: string;
  portOfDischarge: string;
  // ── Account Master, round 2 (Accounts-lifecycle spec audit) ─────────────
  accountCode: string;
  industry: string;
  currency: string;
  paymentTerms: string;
  billingStreet: string; billingCity: string; billingState: string; billingZip: string; billingCountry: string;
  shippingStreet: string; shippingCity: string; shippingState: string; shippingZip: string; shippingCountry: string;
}

const EMPTY: FormState = {
  name: '', company: '', position: '', email: '', phone: '', notes: '',
  gstin: '', panNo: '', creditDays: '',
  creditLimit: '', customerStatus: 'prospect', customerCategoryTier: '', customerRelationshipTier: '',
  companyType: '', website: '', subIndustry: '', cinNumber: '', iecNumber: '', msmeUdyamNumber: '', portOfDischarge: '',
  accountCode: '', industry: '', currency: '', paymentTerms: '',
  billingStreet: '', billingCity: '', billingState: '', billingZip: '', billingCountry: '',
  shippingStreet: '', shippingCity: '', shippingState: '', shippingZip: '', shippingCountry: '',
};

function contactToForm(c: Contact): FormState {
  return {
    name: c.name ?? '',
    company: c.company ?? '',
    position: c.position ?? '',
    email: c.email ?? '',
    phone: c.phone ?? '',
    notes: c.notes ?? '',
    gstin: c.gstin ?? '',
    panNo: c.panNo ?? '',
    creditDays: c.creditDays !== undefined && c.creditDays !== null ? String(c.creditDays) : '',
    creditLimit: c.creditLimit !== undefined && c.creditLimit !== null ? String(c.creditLimit) : '',
    customerStatus: c.customerStatus ?? 'prospect',
    customerCategoryTier: c.customerCategoryTier ?? '',
    customerRelationshipTier: c.customerRelationshipTier ?? '',
    companyType: c.companyType ?? '',
    website: c.website ?? '',
    subIndustry: c.subIndustry ?? '',
    cinNumber: c.cinNumber ?? '',
    iecNumber: c.iecNumber ?? '',
    msmeUdyamNumber: c.msmeUdyamNumber ?? '',
    portOfDischarge: c.portOfDischarge ?? '',
    accountCode: c.accountCode ?? '',
    industry: c.industry ?? '',
    currency: c.currency ?? '',
    paymentTerms: c.paymentTerms ?? '',
    billingStreet: c.billingAddress?.street ?? '',
    billingCity: c.billingAddress?.city ?? '',
    billingState: c.billingAddress?.state ?? '',
    billingZip: c.billingAddress?.zipCode ?? '',
    billingCountry: c.billingAddress?.country ?? '',
    shippingStreet: c.shippingAddress?.street ?? '',
    shippingCity: c.shippingAddress?.city ?? '',
    shippingState: c.shippingAddress?.state ?? '',
    shippingZip: c.shippingAddress?.zipCode ?? '',
    shippingCountry: c.shippingAddress?.country ?? '',
  };
}

const CUSTOMER_STATUS_OPTIONS = [
  { value: 'prospect', label: 'Prospect' },
  { value: 'active', label: 'Active' },
  { value: 'dormant', label: 'Dormant' },
  { value: 'blocked', label: 'Blocked' },
  { value: 'lost', label: 'Lost' },
];
const CATEGORY_TIER_OPTIONS = [
  { value: '', label: 'Not set' },
  { value: 'A', label: 'A' },
  { value: 'B', label: 'B' },
  { value: 'C', label: 'C' },
];
const RELATIONSHIP_TIER_OPTIONS = [
  { value: '', label: 'Not set' },
  { value: 'strategic', label: 'Strategic' },
  { value: 'regular', label: 'Regular' },
  { value: 'small', label: 'Small' },
];

export function ContactFormModal({ open, contact, loading, onClose, onSubmit, initialValues }: ContactFormModalProps) {
  const isEdit = !!contact;
  const [form, setForm] = useState<FormState>(EMPTY);
  const [sameAsBilling, setSameAsBilling] = useState(false);
  const { data: companyTypes = [] } = useMasterItems('company_type');
  const companyTypeOptions = [{ value: '', label: 'Not set' }, ...companyTypes.map((item) => ({ value: item.name, label: item.name }))];

  // Reset the form whenever the modal opens or the target contact changes,
  // adjusting state during render (via previous-value tracking) instead of in an
  // effect to avoid cascading renders.
  const [prevOpen, setPrevOpen] = useState(open);
  const [prevContact, setPrevContact] = useState(contact);
  if (open !== prevOpen || contact !== prevContact) {
    setPrevOpen(open);
    setPrevContact(contact);
    if (open) {
      const nextForm = contact ? contactToForm(contact) : { ...EMPTY, ...initialValues };
      setForm(nextForm);
      setSameAsBilling(
        nextForm.billingStreet === nextForm.shippingStreet
        && nextForm.billingCity === nextForm.shippingCity
        && nextForm.billingState === nextForm.shippingState
        && nextForm.billingZip === nextForm.shippingZip
        && nextForm.billingCountry === nextForm.shippingCountry
        && Boolean(nextForm.billingStreet || nextForm.billingCity || nextForm.billingState || nextForm.billingZip || nextForm.billingCountry),
      );
    }
  }

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const copyBillingToShipping = () => setForm((prev) => ({
    ...prev,
    shippingStreet: prev.billingStreet, shippingCity: prev.billingCity,
    shippingState: prev.billingState, shippingZip: prev.billingZip, shippingCountry: prev.billingCountry,
  }));
  const setBilling = <K extends 'billingStreet' | 'billingCity' | 'billingState' | 'billingZip' | 'billingCountry'>(key: K, value: FormState[K]) => {
    const shippingKey = key.replace('billing', 'shipping') as keyof FormState;
    setForm((prev) => ({ ...prev, [key]: value, ...(sameAsBilling ? { [shippingKey]: value } : {}) }));
  };
  const setShipping = <K extends 'shippingStreet' | 'shippingCity' | 'shippingState' | 'shippingZip' | 'shippingCountry'>(key: K, value: FormState[K]) => {
    setSameAsBilling(false);
    set(key, value);
  };

  const [duplicateInfo, setDuplicateInfo] = useState<{
    on: string;
    type: string;
    match: { _id: string; name: string; company: string; phone: string; gstin: string; ownerName: string };
    message: string;
  } | null>(null);

  const debouncedPhone = useDebounce(form.phone.trim(), 300);
  const debouncedGstin = useDebounce(form.gstin.trim(), 300);

  useEffect(() => {
    if (isEdit) {
      setDuplicateInfo(null);
      return;
    }
    const cleanPhone = debouncedPhone.replace(/\D/g, '');
    const cleanGstin = debouncedGstin.trim().toUpperCase();
    if (cleanPhone.length < 10 && cleanGstin.length < 15) {
      setDuplicateInfo(null);
      return;
    }

    let active = true;
    api.post('/api/contacts-identity/check-duplicate', {
      phone: cleanPhone.length >= 10 ? cleanPhone : undefined,
      gstin: cleanGstin.length >= 15 ? cleanGstin : undefined,
    }).then(({ data }) => {
      if (active) {
        if (data?.duplicate && data?.match) {
          setDuplicateInfo(data);
        } else {
          setDuplicateInfo(null);
        }
      }
    }).catch(() => {
      if (active) setDuplicateInfo(null);
    });

    return () => { active = false; };
  }, [debouncedPhone, debouncedGstin, isEdit]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) return;

    const basic: CreateContactPayload = {
      name: form.name.trim(),
      company: form.company.trim(),
      position: form.position.trim(),
      email: form.email.trim(),
      phone: form.phone.trim(),
      notes: form.notes.trim(),
    };

    // Only send keys the operator actually touched from a neutral baseline —
    // an empty string/blank select means "leave as-is" for a field that was
    // never captured, not "clear it".
    const extended: UpdateContactExtendedPayload = {};
    if (form.gstin.trim()) extended.gstin = form.gstin.trim();
    if (form.panNo.trim()) extended.panNo = form.panNo.trim();
    if (form.creditDays.trim() !== '') extended.creditDays = Number(form.creditDays);
    if (form.creditLimit.trim() !== '') extended.creditLimit = Number(form.creditLimit);
    extended.customerStatus = form.customerStatus;
    if (form.customerCategoryTier) extended.customerCategoryTier = form.customerCategoryTier;
    if (form.customerRelationshipTier) extended.customerRelationshipTier = form.customerRelationshipTier;
    if (form.companyType) extended.companyType = form.companyType;
    if (form.website.trim()) extended.website = form.website.trim();
    if (form.subIndustry.trim()) extended.subIndustry = form.subIndustry.trim();
    if (form.cinNumber.trim()) extended.cinNumber = form.cinNumber.trim();
    if (form.iecNumber.trim()) extended.iecNumber = form.iecNumber.trim();
    if (form.msmeUdyamNumber.trim()) extended.msmeUdyamNumber = form.msmeUdyamNumber.trim();
    if (form.portOfDischarge.trim()) extended.portOfDischarge = form.portOfDischarge.trim();
    if (form.accountCode.trim()) extended.accountCode = form.accountCode.trim();
    if (form.industry.trim()) extended.industry = form.industry.trim();
    if (form.currency.trim()) extended.currency = form.currency.trim();
    if (form.paymentTerms.trim()) extended.paymentTerms = form.paymentTerms.trim();
    const billing = {
      street: form.billingStreet.trim(), city: form.billingCity.trim(), state: form.billingState.trim(),
      zipCode: form.billingZip.trim(), country: form.billingCountry.trim(),
    };
    if (Object.values(billing).some(Boolean)) extended.billingAddress = billing;
    const shipping = {
      street: form.shippingStreet.trim(), city: form.shippingCity.trim(), state: form.shippingState.trim(),
      zipCode: form.shippingZip.trim(), country: form.shippingCountry.trim(),
    };
    if (Object.values(shipping).some(Boolean)) extended.shippingAddress = shipping;

    onSubmit(basic, extended);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEdit ? 'Edit Contact' : 'New Contact'}
      size="lg"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button type="submit" form="contact-form" loading={loading}>
            {isEdit ? 'Save Changes' : 'Create Contact'}
          </Button>
        </>
      }
    >
      <form id="contact-form" onSubmit={handleSubmit} className="space-y-4">
        {duplicateInfo && (
          <div className="rounded-xl border border-amber-300 bg-amber-50 p-3.5 text-body-sm text-amber-950 shadow-xs flex items-start gap-2.5 animate-in fade-in-50 duration-200">
            <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="font-bold text-amber-950 text-body-sm">
                ⚠️ Already registered with {duplicateInfo.match.company || duplicateInfo.match.name}, Owner: {duplicateInfo.match.ownerName}
              </p>
              <p className="text-amber-800 text-caption mt-0.5">
                Matched on {duplicateInfo.on.toUpperCase()} ({duplicateInfo.on === 'phone' ? duplicateInfo.match.phone : duplicateInfo.match.gstin}).
                {duplicateInfo.type === 'contact' ? ' An existing contact already exists in the system.' : ' An existing enquiry is active in the Sales Pipeline.'}
              </p>
            </div>
          </div>
        )}
        <Input
          label="Name *"
          name="contact-name"
          value={form.name}
          onChange={(e) => set('name', e.target.value)}
          placeholder="Full name…"
          required
        />

        <div className="grid grid-cols-2 gap-3">
          <Input
            label="Company"
            name="contact-company"
            value={form.company}
            onChange={(e) => set('company', e.target.value)}
            placeholder="Company…"
          />
          <Input
            label="Position"
            name="contact-position"
            value={form.position}
            onChange={(e) => set('position', e.target.value)}
            placeholder="Job title…"
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Input
            label="Email"
            name="contact-email"
            type="email"
            value={form.email}
            onChange={(e) => set('email', e.target.value)}
            placeholder="email@example.com"
          />
          <Input
            label="Phone"
            name="contact-phone"
            value={form.phone}
            onChange={(e) => set('phone', e.target.value)}
            placeholder="Phone…"
          />
        </div>

        <div>
          <label htmlFor="contact-notes" className="mb-1.5 block text-body-sm font-medium text-on-surface">
            Notes
          </label>
          <textarea
            id="contact-notes"
            rows={3}
            value={form.notes}
            onChange={(e) => set('notes', e.target.value)}
            placeholder="Notes…"
            className="w-full resize-none rounded border border-outline bg-surface px-3 py-2 text-data-md text-on-surface placeholder:text-on-surface-variant/70 focus:outline-none focus:ring-2 focus:ring-primary/40"
          />
        </div>

        {/* ── Identity / Credit (BizPlus parity) ──────────────────────────── */}
        <div className="border-t border-outline pt-4">
          <h3 className="mb-3 text-label-caps uppercase text-on-surface-variant">Identity &amp; Credit</h3>
          <div className="grid grid-cols-2 gap-3">
            <Input label="GSTIN" name="contact-gstin" value={form.gstin} onChange={(e) => set('gstin', e.target.value.toUpperCase())} placeholder="27AAPFU0939F1ZZ" />
            <Input label="PAN" name="contact-pan" value={form.panNo} onChange={(e) => set('panNo', e.target.value.toUpperCase())} placeholder="AAPFU0939F" />
            <Input label="Credit Days" name="contact-credit-days" type="number" value={form.creditDays} onChange={(e) => set('creditDays', e.target.value)} placeholder="30" />
            <Input label="Credit Limit (₹)" name="contact-credit-limit" type="number" value={form.creditLimit} onChange={(e) => set('creditLimit', e.target.value)} placeholder="500000" />
          </div>
          <p className="mt-1.5 text-body-sm text-on-surface-variant">
            Credit Limit here is authoritative — it is synced to the linked Accounting customer record automatically.
          </p>
        </div>

        {/* ── Account Master (client-approved audit) ──────────────────────── */}
        <div className="border-t border-outline pt-4">
          <h3 className="mb-3 text-label-caps uppercase text-on-surface-variant">Account Classification</h3>
          <div className="grid grid-cols-2 gap-3">
            <Select
              label="Customer Status"
              name="contact-customer-status"
              value={form.customerStatus}
              onChange={(e) => set('customerStatus', e.target.value as CustomerStatus)}
              options={CUSTOMER_STATUS_OPTIONS}
            />
            <Select
              label="Category Tier"
              name="contact-category-tier"
              value={form.customerCategoryTier}
              onChange={(e) => set('customerCategoryTier', e.target.value as CustomerCategoryTier | '')}
              options={CATEGORY_TIER_OPTIONS}
            />
            <Select
              label="Relationship Tier"
              name="contact-relationship-tier"
              value={form.customerRelationshipTier}
              onChange={(e) => set('customerRelationshipTier', e.target.value as CustomerRelationshipTier | '')}
              options={RELATIONSHIP_TIER_OPTIONS}
            />
            <Select
              label="Company Type"
              name="contact-company-type"
              value={form.companyType}
              onChange={(e) => set('companyType', e.target.value)}
              options={companyTypeOptions}
            />
            <Input label="Sub-Industry" name="contact-sub-industry" value={form.subIndustry} onChange={(e) => set('subIndustry', e.target.value)} placeholder="e.g. Lubricants" />
            <Input label="Port of Discharge" name="contact-port-of-discharge" value={form.portOfDischarge} onChange={(e) => set('portOfDischarge', e.target.value)} placeholder="For exports…" />
            <Input label="CIN Number" name="contact-cin" value={form.cinNumber} onChange={(e) => set('cinNumber', e.target.value.toUpperCase())} placeholder="Company Identification Number" />
            <Input label="IEC Number" name="contact-iec" value={form.iecNumber} onChange={(e) => set('iecNumber', e.target.value.toUpperCase())} placeholder="Import Export Code" />
            <Input label="MSME/Udyam Number" name="contact-msme" value={form.msmeUdyamNumber} onChange={(e) => set('msmeUdyamNumber', e.target.value.toUpperCase())} placeholder="UDYAM-XX-00-0000000" />
            <Input label="Account / Customer Code" name="contact-account-code" value={form.accountCode} onChange={(e) => set('accountCode', e.target.value)} placeholder="e.g. ACC-0042" />
            <Input label="Industry" name="contact-industry" value={form.industry} onChange={(e) => set('industry', e.target.value)} placeholder="e.g. Automotive" />
            <Input label="Website" name="contact-website" value={form.website} onChange={(e) => set('website', e.target.value)} placeholder="https://example.com" />
          </div>
        </div>

        {/* ── Billing & Shipping (Accounts-lifecycle spec audit) ──────────── */}
        <div className="border-t border-outline pt-4">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="text-label-caps uppercase text-on-surface-variant">Billing Address</h3>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Input label="Street" name="contact-billing-street" value={form.billingStreet} onChange={(e) => setBilling('billingStreet', e.target.value)} />
            <Input label="City" name="contact-billing-city" value={form.billingCity} onChange={(e) => setBilling('billingCity', e.target.value)} />
            <Input label="State" name="contact-billing-state" value={form.billingState} onChange={(e) => setBilling('billingState', e.target.value)} />
            <Input label="Pincode" name="contact-billing-zip" value={form.billingZip} onChange={(e) => setBilling('billingZip', e.target.value)} />
            <Input label="Country" name="contact-billing-country" value={form.billingCountry} onChange={(e) => setBilling('billingCountry', e.target.value)} />
          </div>
        </div>

        <div className="border-t border-outline pt-4">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="text-label-caps uppercase text-on-surface-variant">Shipping Address</h3>
            <label className="flex cursor-pointer items-center gap-2 text-body-sm font-medium text-primary">
              <input type="checkbox" checked={sameAsBilling} onChange={(e) => {
                setSameAsBilling(e.target.checked);
                if (e.target.checked) copyBillingToShipping();
              }} />
              Same as billing address
            </label>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Input label="Street" name="contact-shipping-street" value={form.shippingStreet} onChange={(e) => setShipping('shippingStreet', e.target.value)} />
            <Input label="City" name="contact-shipping-city" value={form.shippingCity} onChange={(e) => setShipping('shippingCity', e.target.value)} />
            <Input label="State" name="contact-shipping-state" value={form.shippingState} onChange={(e) => setShipping('shippingState', e.target.value)} />
            <Input label="Pincode" name="contact-shipping-zip" value={form.shippingZip} onChange={(e) => setShipping('shippingZip', e.target.value)} />
            <Input label="Country" name="contact-shipping-country" value={form.shippingCountry} onChange={(e) => setShipping('shippingCountry', e.target.value)} />
          </div>
        </div>

        {/* ── Currency & Payment Terms (Accounts-lifecycle spec audit) ────── */}
        <div className="border-t border-outline pt-4">
          <h3 className="mb-3 text-label-caps uppercase text-on-surface-variant">Currency &amp; Payment Terms</h3>
          <div className="grid grid-cols-2 gap-3">
            <Input label="Currency" name="contact-currency" value={form.currency} onChange={(e) => set('currency', e.target.value.toUpperCase())} placeholder="INR" maxLength={3} />
            <Input label="Payment Terms" name="contact-payment-terms" value={form.paymentTerms} onChange={(e) => set('paymentTerms', e.target.value)} placeholder="e.g. Net 30" />
          </div>
        </div>
      </form>
    </Modal>
  );
}
